import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Alert,
  Snackbar,
  Chip,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  Visibility,
  Edit,
  Search,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading,
  setError,
  setAgents,
  setSelectedAgent,
  setPage,
  setPageSize,
  setSearch,
  clearSelectedAgent,
  updateAgent as updateAgentAction,
} from '../../store/slices/agentConfigurationsSlice';
import {
  GET_AGENT_CONFIGURATIONS_LIST,
  GET_AGENT_CONFIGURATION,
} from '../../graphql/queries/agentConfigurations.queries';
import { UPDATE_AGENT_CONFIGURATION } from '../../graphql/mutations/agentConfigurations.mutations';
import {
  FindAgentConfigurationListInput,
  FindAgentConfigurationInput,
  UpdateAgentConfigurationInput,
  UpdateAgentConfigurationResponse,
  AgentConfigurationsListResponse,
  AgentConfigurationResponse,
  AgentConfiguration,
} from '../../types/agentConfigurations.types';
import ViewAgentConfigurationModal from '../../components/AgentConfigurations/ViewAgentConfigurationModal';
import EditAgentConfigurationModal, {
  AgentConfigurationFormData,
} from '../../components/AgentConfigurations/EditAgentConfigurationModal';
import MainLayout from '../../components/Layout/MainLayout';
import './AgentConfigurations.css';

const AgentConfigurations: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { agents, loading, error, pagination, search } = useAppSelector(
    (state) => state.agentConfigurations
  );

  const [searchInput, setSearchInput] = useState(search);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [fetchAgents, { loading: fetchingList }] = useLazyQuery<
    { agentConfigurations: AgentConfigurationsListResponse },
    { params: FindAgentConfigurationListInput }
  >(GET_AGENT_CONFIGURATIONS_LIST, { fetchPolicy: 'network-only' });

  const [fetchAgent] = useLazyQuery<
    { agentConfiguration: AgentConfigurationResponse },
    { params: FindAgentConfigurationInput }
  >(GET_AGENT_CONFIGURATION, { fetchPolicy: 'network-only' });

  const [updateAgentMutation] = useMutation<
    { updateAgentConfiguration: UpdateAgentConfigurationResponse },
    { params: UpdateAgentConfigurationInput }
  >(UPDATE_AGENT_CONFIGURATION);

  useEffect(() => {
    loadAgents();
  }, [pagination.page, pagination.pageSize, search]);

  const loadAgents = async () => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchAgents({
        variables: {
          params: {
            search: search || null,
            page: pagination.page,
            pageSize: pagination.pageSize,
          },
        },
      });

      if (data?.agentConfigurations?.success && data.agentConfigurations.data) {
        dispatch(setAgents(data.agentConfigurations.data));
      } else {
        dispatch(setError(data?.agentConfigurations?.message || 'Error al cargar agentes'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSearch = () => {
    dispatch(setSearch(searchInput));
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    dispatch(setPage(model.page + 1));
    dispatch(setPageSize(model.pageSize));
  };

  const handleView = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchAgent({ variables: { params: { ids: [id] } } });
      if (data?.agentConfiguration?.success && data.agentConfiguration.data && data.agentConfiguration.data.length > 0) {
        dispatch(setSelectedAgent(data.agentConfiguration.data[0]));
        setViewModalOpen(true);
      } else {
        dispatch(setError(data?.agentConfiguration?.message || 'Error al cargar agente'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleEdit = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchAgent({ variables: { params: { ids: [id] } } });
      if (data?.agentConfiguration?.success && data.agentConfiguration.data && data.agentConfiguration.data.length > 0) {
        dispatch(setSelectedAgent(data.agentConfiguration.data[0]));
        setSaveError(null);
        setEditModalOpen(true);
      } else {
        dispatch(setError(data?.agentConfiguration?.message || 'Error al cargar agente'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSave = async (formData: AgentConfigurationFormData) => {
    try {
      setSaving(true);
      setSaveError(null);

      const { data: responseData } = await updateAgentMutation({
        variables: {
          params: {
            id: formData.id,
            agentKey: formData.agentKey,
            provider: formData.provider,
            modelName: formData.modelName,
            temperature: formData.temperature,
            maxTokens: formData.maxTokens,
            maxOutputTokens: formData.maxOutputTokens,
            maxRetries: formData.maxRetries,
            topP: formData.topP,
            description: formData.description,
            isEnabled: formData.isEnabled,
          },
        },
      });

      if (responseData?.updateAgentConfiguration?.success) {
        if (responseData.updateAgentConfiguration.data) {
          dispatch(updateAgentAction(responseData.updateAgentConfiguration.data));
        }
        setEditModalOpen(false);
        dispatch(clearSelectedAgent());
        setSaveError(null);
        setSuccessMessage('Configuración de agente actualizada exitosamente');
      } else {
        const msg = responseData?.updateAgentConfiguration?.message || 'Error al actualizar agente';
        const errs = responseData?.updateAgentConfiguration?.errors || [];
        setSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setSaveError(err.message || 'Error de conexión con el servidor');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    dispatch(clearSelectedAgent());
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    dispatch(clearSelectedAgent());
    setSaveError(null);
    setSaving(false);
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };

  const columns: GridColDef<AgentConfiguration>[] = [
    {
      field: 'agentKey',
      headerName: 'Clave del Agente',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Link
          component="button"
          variant="body2"
          underline="hover"
          onClick={() => navigate(`/agent-configurations/${params.row.id}/configuration`)}
          sx={{ cursor: 'pointer' }}
        >
          {params.row.agentKey}
        </Link>
      ),
    },
    { field: 'provider', headerName: 'Proveedor', width: 120 },
    { field: 'modelName', headerName: 'Modelo', width: 150 },
    { field: 'temperature', headerName: 'Temperatura', width: 110 },
    { field: 'maxTokens', headerName: 'Max Tokens', width: 110 },
    { field: 'maxOutputTokens', headerName: 'Max Output Tokens', width: 150 },
    { field: 'maxRetries', headerName: 'Reintentos', width: 100 },
    { field: 'topP', headerName: 'Top P', width: 80 },
    { field: 'description', headerName: 'Descripción', flex: 2, minWidth: 250 },
    {
      field: 'isEnabled',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.isEnabled ? 'Habilitado' : 'Deshabilitado'}
          color={params.row.isEnabled ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    { field: 'createdAt', headerName: 'Fecha Creación', width: 130 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton size="small" color="primary" onClick={() => handleView(params.row.id)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" color="secondary" onClick={() => handleEdit(params.row.id)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="agent-configs-container">
        <Paper elevation={2} className="agent-configs-paper">
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
              Inicio
            </Link>
            <Typography color="text.primary">Agentes</Typography>
          </Breadcrumbs>

          <Box className="agent-configs-header">
            <Typography variant="h4" component="h1">
              Configuración de Agentes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Administración de configuraciones de agentes IA
            </Typography>
          </Box>

          <Box className="agent-configs-toolbar">
            <Box className="search-box">
              <TextField
                size="small"
                placeholder="Buscar agente..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                sx={{ minWidth: 300 }}
              />
              <Button variant="contained" startIcon={<Search />} onClick={handleSearch} sx={{ ml: 1 }}>
                Buscar
              </Button>
            </Box>
          </Box>

          {error && (
            <Box className="error-box">
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <Box className="data-grid-container">
            <DataGrid
              rows={agents}
              columns={columns}
              loading={loading || fetchingList}
              pageSizeOptions={[5, 10, 25, 50]}
              paginationModel={{
                page: pagination.page - 1,
                pageSize: pagination.pageSize,
              }}
              onPaginationModelChange={handlePaginationChange}
              paginationMode="server"
              rowCount={pagination.totalRecords}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                '& .MuiDataGrid-cell:focus': { outline: 'none' },
                '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              }}
            />
          </Box>
        </Paper>

        <ViewAgentConfigurationModal open={viewModalOpen} onClose={handleCloseViewModal} />
        <EditAgentConfigurationModal
          open={editModalOpen}
          onClose={handleCloseEditModal}
          onSave={handleSave}
          saving={saving}
          saveError={saveError}
        />

        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>
      </div>
    </MainLayout>
  );
};

export default AgentConfigurations;
